import { createSlice } from "@reduxjs/toolkit";
import {
  getAreasDesarrolloThunk,
  getAreaDesarrolloByIdThunk,
  updateAreaDesarrolloThunk,
  toggleAreaDesarrolloActiveThunk,
  getAccionesDesarrolloThunk,
  getAccionDesarrolloByIdThunk,
  createAccionDesarrolloThunk,
  createMultiplesAccionesThunk,
  updateAccionDesarrolloThunk,
  toggleAccionDesarrolloActiveThunk,
} from "./fortalezas.thunks";

const initialState = {
  // --- Áreas de Desarrollo ---
  areasList: [],
  areasLoading: false,
  areasError: null,
  areasPage: 1,
  areasPageSize: 10,
  areasTotalPages: 1,
  areasCount: 0,
  areasNext: null,
  areasPrevious: null,
  areasMaxPageReached: 1,
  areasSearch: "",

  selectedArea: null,
  selectedAreaLoading: false,
  selectedAreaError: null,
  areaViewModalOpen: false,

  areaFormModalOpen: false,
  areaFormLoading: false,
  areaFormError: null,

  areaConfirmModalOpen: false,
  toggleAreaId: null,
  areaToggling: false,

  // --- Acciones de Desarrollo ---
  accionesList: [],
  accionesLoading: false,
  accionesError: null,
  accionesPage: 1,
  accionesPageSize: 10,
  accionesTotalPages: 1,
  accionesCount: 0,
  accionesNext: null,
  accionesPrevious: null,
  accionesMaxPageReached: 1,
  accionesSearch: "",
  accionesFilterArea: "",
  accionesFilterTalento: "",

  selectedAccion: null,
  selectedAccionLoading: false,
  selectedAccionError: null,
  accionViewModalOpen: false,

  accionFormModalOpen: false,
  accionFormMode: "create",
  accionFormLoading: false,
  accionFormError: null,

  accionMultiplesModalOpen: false,
  accionMultiplesLoading: false,
  accionMultiplesError: null,

  accionConfirmModalOpen: false,
  toggleAccionId: null,
  accionToggling: false,
};

const fortalezasSlice = createSlice({
  name: "fortalezas",
  initialState,
  reducers: {
    // --- Áreas ---
    clearAreasError: (state) => {
      state.areasError = null;
    },
    setAreasPage: (state, action) => {
      state.areasPage = action.payload;
    },
    setAreasSearch: (state, action) => {
      state.areasSearch = action.payload;
      state.areasPage = 1;
    },
    openAreaViewModal: (state) => {
      state.areaViewModalOpen = true;
    },
    closeAreaViewModal: (state) => {
      state.areaViewModalOpen = false;
      state.selectedArea = null;
    },
    openAreaFormModal: (state) => {
      state.areaFormModalOpen = true;
      state.areaFormError = null;
    },
    closeAreaFormModal: (state) => {
      state.areaFormModalOpen = false;
      state.selectedArea = null;
      state.areaFormError = null;
    },
    openAreaConfirmToggle: (state, action) => {
      state.areaConfirmModalOpen = true;
      state.toggleAreaId = action.payload.id;
    },
    closeAreaConfirmToggle: (state) => {
      state.areaConfirmModalOpen = false;
      state.toggleAreaId = null;
    },

    // --- Acciones ---
    clearAccionesError: (state) => {
      state.accionesError = null;
    },
    setAccionesPage: (state, action) => {
      state.accionesPage = action.payload;
    },
    setAccionesSearch: (state, action) => {
      state.accionesSearch = action.payload;
      state.accionesPage = 1;
    },
    setAccionesFilterArea: (state, action) => {
      state.accionesFilterArea = action.payload;
      state.accionesPage = 1;
    },
    setAccionesFilterTalento: (state, action) => {
      state.accionesFilterTalento = action.payload;
      state.accionesPage = 1;
    },
    clearAccionesFilters: (state) => {
      state.accionesSearch = "";
      state.accionesFilterArea = "";
      state.accionesFilterTalento = "";
      state.accionesPage = 1;
    },
    openAccionViewModal: (state) => {
      state.accionViewModalOpen = true;
    },
    closeAccionViewModal: (state) => {
      state.accionViewModalOpen = false;
      state.selectedAccion = null;
    },
    openAccionFormModal: (state, action) => {
      state.accionFormModalOpen = true;
      state.accionFormMode = action.payload?.mode || "create";
      state.accionFormError = null;
    },
    closeAccionFormModal: (state) => {
      state.accionFormModalOpen = false;
      state.selectedAccion = null;
      state.accionFormError = null;
    },
    openAccionMultiplesModal: (state) => {
      state.accionMultiplesModalOpen = true;
      state.accionMultiplesError = null;
    },
    closeAccionMultiplesModal: (state) => {
      state.accionMultiplesModalOpen = false;
      state.accionMultiplesError = null;
    },
    openAccionConfirmToggle: (state, action) => {
      state.accionConfirmModalOpen = true;
      state.toggleAccionId = action.payload.id;
    },
    closeAccionConfirmToggle: (state) => {
      state.accionConfirmModalOpen = false;
      state.toggleAccionId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== ÁREAS ==========
      // GET ALL
      .addCase(getAreasDesarrolloThunk.pending, (state) => {
        state.areasLoading = true;
        state.areasError = null;
      })
      .addCase(getAreasDesarrolloThunk.fulfilled, (state, action) => {
        const { results, count, next, previous } = action.payload;
        state.areasLoading = false;
        state.areasList = results;
        state.areasCount = count;
        state.areasNext = next;
        state.areasPrevious = previous;
        state.areasMaxPageReached = Math.max(state.areasMaxPageReached, state.areasPage);
      })
      .addCase(getAreasDesarrolloThunk.rejected, (state, action) => {
        state.areasLoading = false;
        state.areasError = action.payload;
      })
      // GET BY ID
      .addCase(getAreaDesarrolloByIdThunk.pending, (state) => {
        state.selectedAreaLoading = true;
        state.selectedAreaError = null;
      })
      .addCase(getAreaDesarrolloByIdThunk.fulfilled, (state, action) => {
        state.selectedAreaLoading = false;
        state.selectedArea = action.payload;
      })
      .addCase(getAreaDesarrolloByIdThunk.rejected, (state, action) => {
        state.selectedAreaLoading = false;
        state.selectedAreaError = action.payload;
      })
      // UPDATE
      .addCase(updateAreaDesarrolloThunk.pending, (state) => {
        state.areaFormLoading = true;
        state.areaFormError = null;
      })
      .addCase(updateAreaDesarrolloThunk.fulfilled, (state, action) => {
        state.areaFormLoading = false;
        const index = state.areasList.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.areasList[index] = action.payload;
        state.areaFormModalOpen = false;
      })
      .addCase(updateAreaDesarrolloThunk.rejected, (state, action) => {
        state.areaFormLoading = false;
        state.areaFormError = action.payload;
      })
      // TOGGLE ACTIVE
      .addCase(toggleAreaDesarrolloActiveThunk.pending, (state) => {
        state.areaToggling = true;
      })
      .addCase(toggleAreaDesarrolloActiveThunk.fulfilled, (state, action) => {
        state.areaToggling = false;
        state.areaConfirmModalOpen = false;
        const index = state.areasList.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.areasList[index] = action.payload;
      })
      .addCase(toggleAreaDesarrolloActiveThunk.rejected, (state, action) => {
        state.areaToggling = false;
        state.areasError = action.payload;
      })

      // ========== ACCIONES ==========
      // GET ALL
      .addCase(getAccionesDesarrolloThunk.pending, (state) => {
        state.accionesLoading = true;
        state.accionesError = null;
      })
      .addCase(getAccionesDesarrolloThunk.fulfilled, (state, action) => {
        const { results, count, next, previous } = action.payload;
        state.accionesLoading = false;
        state.accionesList = results;
        state.accionesCount = count;
        state.accionesNext = next;
        state.accionesPrevious = previous;
        state.accionesMaxPageReached = Math.max(state.accionesMaxPageReached, state.accionesPage);
      })
      .addCase(getAccionesDesarrolloThunk.rejected, (state, action) => {
        state.accionesLoading = false;
        state.accionesError = action.payload;
      })
      // GET BY ID
      .addCase(getAccionDesarrolloByIdThunk.pending, (state) => {
        state.selectedAccionLoading = true;
        state.selectedAccionError = null;
      })
      .addCase(getAccionDesarrolloByIdThunk.fulfilled, (state, action) => {
        state.selectedAccionLoading = false;
        state.selectedAccion = action.payload;
      })
      .addCase(getAccionDesarrolloByIdThunk.rejected, (state, action) => {
        state.selectedAccionLoading = false;
        state.selectedAccionError = action.payload;
      })
      // CREATE
      .addCase(createAccionDesarrolloThunk.pending, (state) => {
        state.accionFormLoading = true;
        state.accionFormError = null;
      })
      .addCase(createAccionDesarrolloThunk.fulfilled, (state) => {
        state.accionFormLoading = false;
        state.accionFormModalOpen = false;
      })
      .addCase(createAccionDesarrolloThunk.rejected, (state, action) => {
        state.accionFormLoading = false;
        state.accionFormError = action.payload;
      })
      // CREATE MULTIPLES
      .addCase(createMultiplesAccionesThunk.pending, (state) => {
        state.accionMultiplesLoading = true;
        state.accionMultiplesError = null;
      })
      .addCase(createMultiplesAccionesThunk.fulfilled, (state) => {
        state.accionMultiplesLoading = false;
        state.accionMultiplesModalOpen = false;
      })
      .addCase(createMultiplesAccionesThunk.rejected, (state, action) => {
        state.accionMultiplesLoading = false;
        state.accionMultiplesError = action.payload;
      })
      // UPDATE
      .addCase(updateAccionDesarrolloThunk.pending, (state) => {
        state.accionFormLoading = true;
        state.accionFormError = null;
      })
      .addCase(updateAccionDesarrolloThunk.fulfilled, (state, action) => {
        state.accionFormLoading = false;
        const index = state.accionesList.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.accionesList[index] = action.payload;
        state.accionFormModalOpen = false;
      })
      .addCase(updateAccionDesarrolloThunk.rejected, (state, action) => {
        state.accionFormLoading = false;
        state.accionFormError = action.payload;
      })
      // TOGGLE ACTIVE
      .addCase(toggleAccionDesarrolloActiveThunk.pending, (state) => {
        state.accionToggling = true;
      })
      .addCase(toggleAccionDesarrolloActiveThunk.fulfilled, (state, action) => {
        state.accionToggling = false;
        state.accionConfirmModalOpen = false;
        const index = state.accionesList.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.accionesList[index] = action.payload;
      })
      .addCase(toggleAccionDesarrolloActiveThunk.rejected, (state, action) => {
        state.accionToggling = false;
        state.accionesError = action.payload;
      });
  },
});

export const {
  // Áreas
  clearAreasError,
  setAreasPage,
  setAreasSearch,
  openAreaViewModal,
  closeAreaViewModal,
  openAreaFormModal,
  closeAreaFormModal,
  openAreaConfirmToggle,
  closeAreaConfirmToggle,
  // Acciones
  clearAccionesError,
  setAccionesPage,
  setAccionesSearch,
  setAccionesFilterArea,
  setAccionesFilterTalento,
  clearAccionesFilters,
  openAccionViewModal,
  closeAccionViewModal,
  openAccionFormModal,
  closeAccionFormModal,
  openAccionMultiplesModal,
  closeAccionMultiplesModal,
  openAccionConfirmToggle,
  closeAccionConfirmToggle,
} = fortalezasSlice.actions;

export default fortalezasSlice.reducer;
